// VFR Devis Digital - Catalogue Service
// Service hybride: donnees statiques (catalogue) + API Dolibarr (clients/devis)
// Pricing 100% API-driven - no hardcoded fallbacks

import type {
  CatalogCategory,
  CatalogProduct,
  CatalogCategoryId,
  ProductConfiguration,
  CalculatedPrice,
  CATEGORY_DOLIBARR_MAP
} from '../types/catalogue'

// Import du catalogue statique (sera genere depuis le JSON)
import catalogueData from '../data/catalogue-complet.json'

// ============================================
// Catalogue Data Access
// ============================================

class CatalogueService {
  private categories: CatalogCategory[] = []
  private products: CatalogProduct[] = []
  private productsByCategory: Map<CatalogCategoryId, CatalogProduct[]> = new Map()
  private initialized = false
  private _dataSource: 'api' | 'static' = 'static'

  get dataSource(): 'api' | 'static' {
    return this._dataSource
  }

  /**
   * Initialize le service avec les donnees du catalogue
   * Tente d'abord l'API backend, puis fallback sur le JSON statique
   */
  async init(): Promise<void> {
    if (this.initialized) return

    // Try API first
    try {
      const response = await fetch('/api/v1/catalogue/full', {
        signal: AbortSignal.timeout(5000)
      })
      if (response.ok) {
        const apiData = await response.json()
        if (apiData.categories && apiData.categories.length > 0) {
          this.loadFromData(apiData.categories)
          this._dataSource = 'api'
          this.initialized = true
          console.log(`[CatalogueService] Loaded from API (${this.products.length} products)`)
          return
        }
      }
    } catch {
      // API not available, fall through to static
    }

    // Fallback to static JSON
    this.loadFromData(catalogueData.categories)
    this._dataSource = 'static'
    this.initialized = true
    console.log(`[CatalogueService] Loaded from static JSON (${this.products.length} products)`)
  }

  /**
   * Charge les donnees depuis un tableau de categories (API ou statique)
   */
  private loadFromData(categories: typeof catalogueData.categories): void {
    this.categories = categories.map(cat => ({
      id: cat.id as CatalogCategoryId,
      name: cat.name,
      order: cat.order,
      productCount: cat.productCount,
      dolibarrId: this.getCategoryDolibarrId(cat.id as CatalogCategoryId)
    }))

    this.products = []
    categories.forEach(cat => {
      cat.products.forEach(prod => {
        this.products.push(prod as unknown as CatalogProduct)
      })
    })

    this.productsByCategory.clear()
    this.categories.forEach(cat => {
      const catProducts = categories
        .find(c => c.id === cat.id)
        ?.products || []
      this.productsByCategory.set(cat.id, catProducts as unknown as CatalogProduct[])
    })
  }

  private getCategoryDolibarrId(categoryId: CatalogCategoryId): string {
    const map: Record<CatalogCategoryId, string> = {
      'grand-format': '2',
      'imprimerie': '3',
      'textile': '4',
      'enseigne': '5',
      'objet': '6',
      'service': '7'
    }
    return map[categoryId] || ''
  }

  // ============================================
  // Categories
  // ============================================

  getCategories(): CatalogCategory[] {
    return this.categories.sort((a, b) => a.order - b.order)
  }

  getCategoryById(id: CatalogCategoryId): CatalogCategory | undefined {
    return this.categories.find(cat => cat.id === id)
  }

  // ============================================
  // Products
  // ============================================

  getProductsByCategory(categoryId: CatalogCategoryId): CatalogProduct[] {
    return this.productsByCategory.get(categoryId) || []
  }

  getProductById(productId: string): CatalogProduct | undefined {
    return this.products.find(prod => prod.id === productId)
  }

  searchProducts(query: string): CatalogProduct[] {
    const lowerQuery = query.toLowerCase()
    return this.products.filter(prod =>
      prod.name.toLowerCase().includes(lowerQuery) ||
      prod.category.toLowerCase().includes(lowerQuery)
    )
  }

  // ============================================
  // Price Calculation
  // ============================================

  calculatePrice(config: ProductConfiguration): CalculatedPrice {
    const product = this.getProductById(config.productId)
    if (!product) {
      return {
        totalHT: 0,
        totalTTC: 0,
        breakdown: [],
        unit: 'EUR'
      }
    }

    const breakdown: Array<{ label: string; value: number }> = []
    let totalHT = 0
    let prixSurDemande = false

    switch (product.type) {
      case 'surface':
        ({ total: totalHT, prixSurDemande } = this.calculateSurfacePrice(product, config, breakdown))
        break

      case 'format-predefini':
      case 'format-predefini-quantite':
        ({ total: totalHT, prixSurDemande } = this.calculateFormatPrice(product, config, breakdown))
        break

      case 'forfait':
        ({ total: totalHT, prixSurDemande } = this.calculateForfaitPrice(product, config, breakdown))
        break

      case 'temps':
        totalHT = this.calculateTempsPrice(product, config, breakdown)
        break

      case 'covering-vehicule':
        ({ total: totalHT, prixSurDemande } = this.calculateCoveringVehiculePrice(product, config, breakdown))
        break

      default:
        // Fallback: use example price if available
        totalHT = product.pricing.totalHT || 0
        if (totalHT === 0) prixSurDemande = true
    }

    // Add service costs (graphisme, pose, etc.) for all types except:
    // - covering-vehicule: handles them internally
    // - types with optionPricing already handled by calculateFromOptionPricing()
    const hasOptionPricingHandled = (product.type === 'forfait' || product.type === 'format-predefini') &&
      product.declinations.some(d => d.optionPricing)
    if (product.type !== 'covering-vehicule' && !hasOptionPricingHandled) {
      totalHT += this.addServiceCosts(product, config, breakdown)
    }

    // Apply volume discount/surcharge (tranchRem) based on surface
    if (product.tranchRem?.length) {
      const surfaceM2 = config.dimensions.surface ||
        (config.dimensions.largeur && config.dimensions.hauteur
          ? (config.dimensions.largeur * config.dimensions.hauteur) / 1000000
          : 0)
      if (surfaceM2 > 0) {
        const tranche = product.tranchRem.find(([min, max]) =>
          surfaceM2 >= min && (max === null || surfaceM2 < max)
        )
        if (tranche && tranche[2] != null && tranche[2] !== 0) {
          const remisePct = tranche[2]
          const adjustmentFactor = 1 - remisePct / 100
          const adjustment = totalHT * (adjustmentFactor - 1)
          const label = remisePct > 0
            ? `Remise volume -${remisePct}% (${surfaceM2.toFixed(2)} m²)`
            : `Majoration petite surface +${Math.abs(remisePct)}% (${surfaceM2.toFixed(2)} m²)`
          breakdown.push({ label, value: adjustment })
          totalHT *= adjustmentFactor
        }
      }
    }

    // Extract fixed setup costs (tempsReg) — they don't scale with quantity
    const setupCosts = breakdown
      .filter(b => b.label.startsWith('Reglage '))
      .reduce((sum, b) => sum + b.value, 0)

    // Apply quantity to variable costs only, then add back fixed setup costs
    const variableHT = totalHT - setupCosts
    totalHT = (variableHT * config.quantity) + setupCosts

    // Calculate TTC
    const tva = product.pricing.tva || 20
    const totalTTC = totalHT * (1 + tva / 100)

    return {
      totalHT: Math.round(totalHT * 100) / 100,
      totalTTC: Math.round(totalTTC * 100) / 100,
      breakdown,
      unit: product.pricing.unit || 'EUR',
      ...(prixSurDemande ? { prixSurDemande: true } : {})
    }
  }

  /**
   * Ajoute les couts de services transverses (graphisme, pose)
   * Uses optionPricing from API declinations only
   */
  private addServiceCosts(
    product: CatalogProduct,
    config: ProductConfiguration,
    breakdown: Array<{ label: string; value: number }>
  ): number {
    let total = 0

    // Compute surface for m²-based costs (relevant for surface products)
    const surfaceM2 = config.dimensions.surface ||
      (config.dimensions.largeur && config.dimensions.hauteur
        ? (config.dimensions.largeur * config.dimensions.hauteur) / 1000000
        : 0)

    for (const dec of product.declinations) {
      if (!dec.optionPricing) continue

      const selectedOption = config.declinations[dec.id] as string | undefined
      if (!selectedOption) continue

      const pricing = dec.optionPricing[selectedOption]
      if (!pricing) continue

      if (pricing.prixFixe) {
        breakdown.push({ label: selectedOption, value: pricing.prixFixe })
        total += pricing.prixFixe
      }
      if (pricing.prixUnitaire) {
        breakdown.push({ label: `${dec.name}: ${selectedOption}`, value: pricing.prixUnitaire })
        total += pricing.prixUnitaire
      }
      if (pricing.prixMachinePiece) {
        breakdown.push({ label: `Machine (${selectedOption})`, value: pricing.prixMachinePiece })
        total += pricing.prixMachinePiece
      }
      if (surfaceM2 && pricing.prixMachineM2) {
        const cost = pricing.prixMachineM2 * surfaceM2
        breakdown.push({ label: `${dec.name} (${surfaceM2.toFixed(2)} m²)`, value: cost })
        total += cost
      }
      if (surfaceM2 && pricing.prixEncreM2) {
        const cost = pricing.prixEncreM2 * surfaceM2
        breakdown.push({ label: `Encre ${selectedOption} (${surfaceM2.toFixed(2)} m²)`, value: cost })
        total += cost
      }
      if (pricing.tempsRegCost) {
        breakdown.push({ label: `Reglage ${selectedOption}`, value: pricing.tempsRegCost })
        total += pricing.tempsRegCost
      }
    }

    return total
  }

  private calculateSurfacePrice(
    product: CatalogProduct,
    config: ProductConfiguration,
    breakdown: Array<{ label: string; value: number }>
  ): { total: number; prixSurDemande: boolean } {
    // Calculate fourreau costs (EUR amount) + collect machine security margins
    let fourreauTotal = 0
    let maxMargesec = 0
    const largeur = config.dimensions.largeur
    const hauteur = config.dimensions.hauteur
    for (const dec of product.declinations) {
      if (!dec.optionPricing) continue
      const selected = config.declinations[dec.id] as string
      if (!selected) continue
      const pricing = dec.optionPricing[selected]
      // Fourreau: exact FP formula = eurPerMl * (smallSide*mult + largeSide*mult) / 1000
      if (pricing?.fourreauFormula) {
        const f = pricing.fourreauFormula
        const smallSide = Math.min(largeur, hauteur)
        const largeSide = Math.max(largeur, hauteur)
        const ml = (smallSide * f.smallSideMultiplier + largeSide * f.largeSideMultiplier) / 1000
        const cost = f.eurPerMl * ml
        if (cost > 0) {
          fourreauTotal += cost
          breakdown.push({ label: `Fourreau ${selected} (${ml.toFixed(2)} ml)`, value: cost })
        }
      }
      if (pricing?.machineMargesecMm && pricing.machineMargesecMm > maxMargesec) {
        maxMargesec = pricing.machineMargesecMm
      }
    }

    // Calculate surface in m2 (with machine security margins for material consumption)
    let effectiveLargeur = largeur
    let effectiveHauteur = hauteur
    if (maxMargesec > 0) {
      effectiveLargeur += maxMargesec * 2
      effectiveHauteur += maxMargesec * 2
    }
    const surface = config.dimensions.surface ||
      (effectiveLargeur * effectiveHauteur) / 1000000

    // Strategy: use machine formula ONLY if it includes material/ink costs
    // (i.e. it's a complete cost model). Otherwise use the prix/m2 reference
    // which already includes material + machine + margin.
    const machines = product.pricingFormula?.machines
    const hasCompleteCostModel = machines?.some(m =>
      m.calculMatiere?.prixParM2 || m.coutEncre?.prixParM2
    )

    if (machines && hasCompleteCostModel) {
      // Complete formula: sum all machine costs (material + ink + time)
      let total = 0
      machines.forEach(machine => {
        if (machine.calculMatiere?.prixParM2) {
          const matiereCost = surface * machine.calculMatiere.prixParM2
          breakdown.push({ label: `Matiere (${machine.id})`, value: matiereCost })
          total += matiereCost
        }
        if (machine.coutEncre?.prixParM2) {
          const encreCost = surface * machine.coutEncre.prixParM2
          breakdown.push({ label: 'Encre', value: encreCost })
          total += encreCost
        }
        if (machine.coutFixe) {
          breakdown.push({ label: machine.id, value: machine.coutFixe })
          total += machine.coutFixe
        }
        if (machine.coutsMachine) {
          const fixedCost = machine.coutsMachine.fixe || 0
          const timeCostPerM2 = machine.coutsMachine.coutTemps || 0
          const machineCost = fixedCost + (timeCostPerM2 * surface)
          if (machineCost > 0) {
            breakdown.push({ label: `Machine ${machine.id}`, value: machineCost })
            total += machineCost
          }
        }
      })

      // Machine formula covers labor + ink + (sometimes) material from the
      // primary printing machine. The physical support (panneau rigide,
      // bâche, etc.) is modeled as `prixMatiereM2` on a declination option
      // and lives outside the machine formula — add it here so the user's
      // choice of support actually impacts the price.
      if (total > 0 && surface > 0) {
        for (const dec of product.declinations) {
          if (!dec.optionPricing) continue
          const selected = config.declinations[dec.id] as string | undefined
          if (!selected) continue
          const pricing = dec.optionPricing[selected]
          if (pricing?.prixMatiereM2) {
            const cost = pricing.prixMatiereM2 * surface
            breakdown.push({ label: `Matiere ${selected} (${surface.toFixed(2)} m²)`, value: cost })
            total += cost
          }
        }
      }

      if (total > 0) return { total: total + fourreauTotal, prixSurDemande: false }
    }

    // No complete formula: try option pricing from declinations (e.g. adhesif teinte masse)
    // This handles products where cost = sum of selected machine/process options per m²
    const optionTotal = this.calculateFromOptionPricing(product, config, breakdown, surface)
    if (optionTotal > 0) return { total: optionTotal + fourreauTotal, prixSurDemande: false }

    // Still nothing: use reference price per m2
    if (product.pricing.exemple1m2) {
      const total = surface * (product.pricing.exemple1m2.totalHT || 0)
      breakdown.push({ label: `Prix @ ${product.pricing.exemple1m2.totalHT} EUR/m²`, value: total })
      return { total: total + fourreauTotal, prixSurDemande: false }
    }

    const pricing = product.pricing as Record<string, unknown>
    const exempleKey = Object.keys(pricing).find(k => k.startsWith('exemple'))
    if (exempleKey) {
      const exempleValue = pricing[exempleKey] as { totalHT?: number } | undefined
      if (exempleValue?.totalHT) {
        const total = surface * exempleValue.totalHT
        breakdown.push({ label: `Prix @ ${exempleValue.totalHT} EUR/m²`, value: total })
        return { total: total + fourreauTotal, prixSurDemande: false }
      }
    }

    // No pricing data available
    return { total: fourreauTotal || 0, prixSurDemande: fourreauTotal === 0 }
  }

  private calculateFormatPrice(
    product: CatalogProduct,
    config: ProductConfiguration,
    breakdown: Array<{ label: string; value: number }>
  ): { total: number; prixSurDemande: boolean } {
    // Get format price from pricing formula tranches
    if (product.pricingFormula?.tranches) {
      const qty = config.quantity
      const tranche = product.pricingFormula.tranches.find(t =>
        qty >= t.min && (t.max === null || qty <= t.max)
      )
      if (tranche) {
        breakdown.push({ label: `Prix unitaire (tranche ${tranche.min}-${tranche.max || '+'})`, value: tranche.prixUnitaire })
        return { total: tranche.prixUnitaire, prixSurDemande: false }
      }
    }

    // Strategy: compute from declination optionPricing (API-driven, like forfait)
    // For format-predefini, use the selected format's surface for m²-based costs
    const hasOptionPricing = product.declinations.some(d => d.optionPricing)
    if (hasOptionPricing) {
      const surfaceM2 = config.dimensions.surface ||
        (config.dimensions.largeur && config.dimensions.hauteur
          ? (config.dimensions.largeur * config.dimensions.hauteur) / 1000000
          : 0)
      const total = this.calculateFromOptionPricing(product, config, breakdown, surfaceM2 || undefined)
      if (total > 0) return { total, prixSurDemande: false }
    }

    // Fallback to example price from exemples object
    const exemples = product.pricing.exemples || {}
    const firstExemple = Object.values(exemples)[0]
    if (firstExemple) {
      breakdown.push({ label: 'Prix piece', value: firstExemple.totalHT })
      return { total: firstExemple.totalHT, prixSurDemande: false }
    }

    // Fallback: look for any exemple* key in pricing (e.g., exemple55x85mm, exemple1m2)
    const pricing = product.pricing as Record<string, unknown>
    const exempleKey = Object.keys(pricing).find(k => k.startsWith('exemple'))
    if (exempleKey) {
      const exempleValue = pricing[exempleKey] as { totalHT?: number } | undefined
      if (exempleValue?.totalHT) {
        breakdown.push({ label: 'Prix piece', value: exempleValue.totalHT })
        return { total: exempleValue.totalHT, prixSurDemande: false }
      }
    }

    // Check for fixed totalHT
    if (product.pricing.totalHT) {
      breakdown.push({ label: 'Prix piece', value: product.pricing.totalHT })
      return { total: product.pricing.totalHT, prixSurDemande: false }
    }

    // No pricing data available
    return { total: 0, prixSurDemande: true }
  }

  private calculateForfaitPrice(
    product: CatalogProduct,
    config: ProductConfiguration,
    breakdown: Array<{ label: string; value: number }>
  ): { total: number; prixSurDemande: boolean } {
    // Strategy 1: Use optionPricing from declinations (API-driven, most accurate)
    const hasOptionPricing = product.declinations.some(d => d.optionPricing)
    if (hasOptionPricing) {
      const total = this.calculateFromOptionPricing(product, config, breakdown)
      if (total > 0) return { total, prixSurDemande: false }
    }

    // Strategy 2: Use pricingFormula forfaits (legacy)
    const forfaitDecId = Object.keys(config.declinations).find(key => key.includes('forfait'))
    const forfaitSelection = forfaitDecId ? config.declinations[forfaitDecId] as string : null

    if (forfaitSelection && product.pricingFormula?.forfaits) {
      const forfaitKey = this.mapForfaitSelectionToKey(forfaitSelection, product.pricingFormula.forfaits)
      if (forfaitKey) {
        const forfait = product.pricingFormula.forfaits[forfaitKey]
        if (forfait) {
          breakdown.push({ label: forfaitSelection, value: forfait.prix })
          return { total: forfait.prix, prixSurDemande: false }
        }
      }
    }

    // Strategy 3: Fixed price from API/static
    if (product.pricing.totalHT) {
      breakdown.push({ label: 'Forfait', value: product.pricing.totalHT })
      return { total: product.pricing.totalHT, prixSurDemande: false }
    }

    // No pricing data available
    return { total: 0, prixSurDemande: true }
  }

  /**
   * Calculate price from declination optionPricing (API-driven)
   * Sums prixUnitaire + prixMachinePiece + prixFixe from selected options
   * If surfaceM2 is provided, also multiplies prixMachineM2/prixEncreM2 by the surface
   */
  private calculateFromOptionPricing(
    product: CatalogProduct,
    config: ProductConfiguration,
    breakdown: Array<{ label: string; value: number }>,
    surfaceM2?: number
  ): number {
    let total = 0

    for (const dec of product.declinations) {
      if (!dec.optionPricing) continue

      const selectedOption = config.declinations[dec.id] as string
      if (!selectedOption) continue

      const pricing = dec.optionPricing[selectedOption]
      if (!pricing) continue
      let skipMatiereUnit = false

      // Tranche-based pricing overrides standard prixUnitaire/prixFixe
      if (pricing.trancheType && pricing.trancheDivisor != null) {
        const divisor = pricing.trancheDivisor || 1
        const qty = config.quantity
        let cost = 0
        switch (pricing.trancheType) {
          case 'peripherie': {
            // Cost per perimeter segment (divisor in mm)
            const perimetre = 2 * ((config.dimensions.largeur || 0) + (config.dimensions.hauteur || 0))
            if (perimetre > 0 && divisor > 0) {
              cost = (pricing.prixUnitaire || 0) * (perimetre / divisor)
              breakdown.push({ label: `${selectedOption} (perim. ${perimetre}mm / ${divisor}mm)`, value: cost })
            }
            break
          }
          case 'support': {
            // Cost per lot of N sheets
            if (divisor > 0) {
              cost = (pricing.prixFixe || 0) * Math.ceil(qty / divisor)
              breakdown.push({ label: `${selectedOption} (par lot de ${divisor})`, value: cost })
            }
            break
          }
          case 'qt': {
            if (divisor === 0) {
              // Forfait fixe (e.g., RIP) — does not multiply by quantity
              cost = pricing.prixFixe || pricing.prixUnitaire || 0
              if (cost > 0) breakdown.push({ label: `${selectedOption} (forfait)`, value: cost })
            } else {
              // Standard per-piece (already handled by prixUnitaire below)
              if (pricing.prixUnitaire) {
                breakdown.push({ label: `${dec.name}: ${selectedOption}`, value: pricing.prixUnitaire })
                cost = pricing.prixUnitaire
              }
            }
            break
          }
          case 'prix':
          default: {
            // Direct price contribution
            if (pricing.prixFixe) {
              breakdown.push({ label: selectedOption, value: pricing.prixFixe })
              cost = pricing.prixFixe
            } else if (pricing.prixUnitaire) {
              breakdown.push({ label: `${dec.name}: ${selectedOption}`, value: pricing.prixUnitaire })
              cost = pricing.prixUnitaire
            }
            break
          }
        }
        total += cost
      } else {
        // Standard pricing (no tranche)
        if (pricing.prixUnitaire) {
          breakdown.push({ label: `${dec.name}: ${selectedOption}`, value: pricing.prixUnitaire })
          total += pricing.prixUnitaire
        }
        if (pricing.prixFixe) {
          breakdown.push({ label: selectedOption, value: pricing.prixFixe })
          total += pricing.prixFixe
        }
      }

      if (pricing.prixMachinePiece) {
        breakdown.push({ label: `Machine (${selectedOption})`, value: pricing.prixMachinePiece })
        total += pricing.prixMachinePiece
      }
      if (surfaceM2 && pricing.prixMachineM2) {
        const cost = pricing.prixMachineM2 * surfaceM2
        breakdown.push({ label: `Machine ${selectedOption} (${surfaceM2.toFixed(2)} m²)`, value: cost })
        total += cost
      }
      if (surfaceM2 && pricing.prixEncreM2) {
        const cost = pricing.prixEncreM2 * surfaceM2
        breakdown.push({ label: `Encre ${selectedOption} (${surfaceM2.toFixed(2)} m²)`, value: cost })
        total += cost
      }
      // Click cost (digital presses: cost per impression * sheets + waste)
      if (pricing.coutClicUnit && pricing.coutClicUnit > 0) {
        const qty = config.quantity
        const gache = (pricing.gache || 0) + (pricing.gacheFixe || 0)

        // For format-predefini: compute poses per sheet (imposition)
        // Piece dimensions from config, sheet = A4 (210x297mm) default
        let sheets = qty
        const pieceW = config.dimensions.largeur
        const pieceH = config.dimensions.hauteur
        if (pieceW > 0 && pieceH > 0 && pieceW < 210 && pieceH < 297) {
          // Small format: compute how many pieces fit on a sheet
          const sheetW = 210 // A4 width mm (default, could come from stock)
          const sheetH = 297 // A4 height mm
          const posesNormal = Math.floor(sheetW / pieceW) * Math.floor(sheetH / pieceH)
          const posesRotated = Math.floor(sheetW / pieceH) * Math.floor(sheetH / pieceW)
          const poses = Math.max(posesNormal, posesRotated, 1)
          sheets = Math.ceil(qty / poses)
          breakdown.push({ label: `Imposition: ${poses} poses/feuille`, value: 0 })
        }

        const totalSheets = sheets + gache
        const baseClic = totalSheets * pricing.coutClicUnit * (pricing.margeFraisClics || 1)
        // Apply degressive margin from tranchMargeQtImp
        let tranchMarge = 0
        if (pricing.tranchMargeQtImp) {
          const tranche = pricing.tranchMargeQtImp.find(([min, max]) => totalSheets >= min && totalSheets < max)
          if (tranche) tranchMarge = tranche[2]
        }
        const clicCost = baseClic * (1 + tranchMarge / 100)
        breakdown.push({ label: `Clics ${selectedOption} (${totalSheets} feuilles)`, value: clicCost })
        total += clicCost

        // Paper cost: multiply by sheets instead of pieces
        if (pricing.prixMatiereUnit) {
          const paperCost = pricing.prixMatiereUnit * totalSheets
          breakdown.push({ label: `Papier (${totalSheets} feuilles)`, value: paperCost })
          total += paperCost
          skipMatiereUnit = true
        }
      }
      // Material cost from stock
      if (surfaceM2 && pricing.prixMatiereM2) {
        const cost = pricing.prixMatiereM2 * surfaceM2
        breakdown.push({ label: `Matiere ${selectedOption} (${surfaceM2.toFixed(2)} m²)`, value: cost })
        total += cost
      }
      if (pricing.prixMatiereUnit && !skipMatiereUnit) {
        breakdown.push({ label: `Matiere ${selectedOption}`, value: pricing.prixMatiereUnit })
        total += pricing.prixMatiereUnit
      }
      // Setup time: fixed cost per job per machine (does not scale with surface or quantity)
      if (pricing.tempsRegCost) {
        breakdown.push({ label: `Reglage ${selectedOption}`, value: pricing.tempsRegCost })
        total += pricing.tempsRegCost
      }
    }

    // If optionPricing yielded nothing, fall back to totalHT
    if (total === 0 && product.pricing.totalHT) {
      breakdown.push({ label: 'Forfait', value: product.pricing.totalHT })
      return product.pricing.totalHT
    }

    return total
  }

  /**
   * Maps a forfait selection string to the corresponding key in forfaits object
   * Handles variations like "1 demi-heure" -> "demi-heure", "30 minutes" -> "demi-heure"
   */
  private mapForfaitSelectionToKey(
    selection: string,
    forfaits: Record<string, { duree?: number; prix: number }>
  ): string | null {
    const normalizedSelection = selection.toLowerCase().trim()

    // Direct match first
    if (forfaits[normalizedSelection]) {
      return normalizedSelection
    }

    // Try removing leading number (e.g., "1 demi-heure" -> "demi-heure")
    const withoutNumber = normalizedSelection.replace(/^\d+\s*/, '').replace(/\s+/g, '-')
    if (forfaits[withoutNumber]) {
      return withoutNumber
    }

    // Try matching by duration keywords
    for (const [key, forfait] of Object.entries(forfaits)) {
      // Check if selection contains the key
      if (normalizedSelection.includes(key.replace('-', ' ')) ||
          normalizedSelection.includes(key)) {
        return key
      }
      // Check duration-based matching (30 min = demi-heure, 60 min = heure)
      if (forfait.duree) {
        if (normalizedSelection.includes('30') && forfait.duree === 30) return key
        if (normalizedSelection.includes('60') && forfait.duree === 60) return key
        if (normalizedSelection.includes('demi') && forfait.duree === 30) return key
        if ((normalizedSelection.includes('heure') && !normalizedSelection.includes('demi')) && forfait.duree === 60) return key
      }
    }

    return null
  }

  private calculateTempsPrice(
    product: CatalogProduct,
    config: ProductConfiguration,
    breakdown: Array<{ label: string; value: number }>
  ): number {
    const temps = config.declinations['temps'] as number || 60
    const tauxHoraire = product.pricingFormula?.tauxHoraire || 45

    const tempsPrice = (temps / 60) * tauxHoraire
    breakdown.push({ label: `${temps} min @ ${tauxHoraire} EUR/h`, value: tempsPrice })

    let total = tempsPrice

    // Add variable costs if present
    const variableCosts = config.declinations['variableCosts'] as Array<{
      service: string
      cout: number
      marge: number
      quantite: number
      totalHT: number
    }> | undefined

    if (variableCosts && Array.isArray(variableCosts)) {
      variableCosts.forEach(cost => {
        if (cost.service && cost.totalHT > 0) {
          breakdown.push({ label: cost.service, value: cost.totalHT })
          total += cost.totalHT
        }
      })
    }

    return total
  }

  // Story 9.6: Calcul prix covering vehicule
  private calculateCoveringVehiculePrice(
    product: CatalogProduct,
    config: ProductConfiguration,
    breakdown: Array<{ label: string; value: number }>
  ): { total: number; prixSurDemande: boolean } {
    // Get surface from dimensions (set by ConfiguratorCoveringVehicule)
    const surface = config.dimensions.surface ||
      (config.dimensions.largeur * config.dimensions.hauteur) / 1000000

    let total = 0

    // Strategy 1: legacy pricingFormula.machines approach
    let prixM2: number | null = null
    if (product.pricingFormula?.machines?.[0]?.calculMatiere?.prixParM2) {
      prixM2 = product.pricingFormula.machines[0].calculMatiere.prixParM2
    }
    if (prixM2 === null && product.pricing.exemple1m2?.totalHT) {
      prixM2 = product.pricing.exemple1m2.totalHT
    }
    if (prixM2 !== null) {
      const baseCost = surface * prixM2
      breakdown.push({ label: `Surface ${surface.toFixed(2)} m² @ ${prixM2} EUR/m²`, value: baseCost })
      total += baseCost
    }

    // Strategy 2 (& complement): compute from declination optionPricing
    // Handles prixFixe, prixUnitaire, prixMachinePiece, prixMachineM2, prixEncreM2
    for (const dec of product.declinations) {
      if (!dec.optionPricing) continue

      const selectedOption = config.declinations[dec.id] as string | undefined
      if (!selectedOption) continue

      const pricing = dec.optionPricing[selectedOption]
      if (!pricing) continue

      if (pricing.prixFixe) {
        breakdown.push({ label: selectedOption, value: pricing.prixFixe })
        total += pricing.prixFixe
      }
      if (pricing.prixUnitaire) {
        breakdown.push({ label: `${dec.name}: ${selectedOption}`, value: pricing.prixUnitaire })
        total += pricing.prixUnitaire
      }
      if (pricing.prixMachinePiece) {
        breakdown.push({ label: `Machine (${selectedOption})`, value: pricing.prixMachinePiece })
        total += pricing.prixMachinePiece
      }
      if (surface && pricing.prixMachineM2) {
        const cost = pricing.prixMachineM2 * surface
        breakdown.push({ label: `${dec.name} (${surface.toFixed(2)} m²)`, value: cost })
        total += cost
      }
      if (surface && pricing.prixEncreM2) {
        const cost = pricing.prixEncreM2 * surface
        breakdown.push({ label: `Encre ${selectedOption} (${surface.toFixed(2)} m²)`, value: cost })
        total += cost
      }
    }

    if (total === 0) {
      return { total: 0, prixSurDemande: true }
    }

    return { total, prixSurDemande: false }
  }

  // ============================================
  // Configuration Helpers
  // ============================================

  getDefaultConfiguration(productId: string): ProductConfiguration | null {
    const product = this.getProductById(productId)
    if (!product) return null

    const declinations: ProductConfiguration['declinations'] = {}

    product.declinations.forEach(dec => {
      declinations[dec.id] = dec.default
    })

    return {
      productId,
      declinations,
      dimensions: {
        largeur: 100,
        hauteur: 100,
        surface: 0.01
      },
      quantity: 1
    }
  }

  formatDeclinationsSummary(config: ProductConfiguration): string {
    const product = this.getProductById(config.productId)
    if (!product) return ''

    const parts: string[] = []

    product.declinations.forEach(dec => {
      const value = config.declinations[dec.id]
      if (value && value !== dec.default) {
        if (Array.isArray(value)) {
          parts.push(`${dec.name}: ${value.join(', ')}`)
        } else {
          parts.push(`${dec.name}: ${value}`)
        }
      }
    })

    return parts.join(' | ')
  }
}

// Singleton export
export const catalogueService = new CatalogueService()
export default catalogueService
